import * as yup from "yup";
import {
  fieldMaxLength,
  fieldMustBeANumber,
  fieldMustBePositive,
  requiredField,
} from "../../../../common/infrastructure/helpers/yupHelper";

export const GIFT_VOUCHER_NAME_MAX_LENGTH = 200;

/**
 * Create / edit form for a voucher that has NOT been sold yet. Once it is sold its face
 * value is a booked liability and its code is printed and in someone's hands, so the API
 * refuses the edit (32006) and the UI never opens this form for it.
 *
 * "Валиден до" is optional: empty means the voucher never expires. A date in the past is
 * rejected here as well as server-side, so the staff member finds out before saving.
 */
export const giftVoucherFormSchema = yup.object({
  name: yup
    .string()
    .trim()
    .max(
      GIFT_VOUCHER_NAME_MAX_LENGTH,
      fieldMaxLength("Име", GIFT_VOUCHER_NAME_MAX_LENGTH),
    )
    .required(requiredField("Име")),
  amount: yup
    .number()
    .typeError(fieldMustBeANumber("Стойност"))
    .positive(fieldMustBePositive("Стойност"))
    .required(requiredField("Стойност")),
  validUntil: yup
    .date()
    .nullable()
    .test(
      "not-in-the-past",
      "Срокът на валидност не може да е в миналото.",
      // "Today" is computed at VALIDATION time, not when this module loads — a till left
      // open overnight would otherwise keep accepting yesterday. Compared against the
      // start of today so a voucher may be valid through the whole of today, exactly as
      // the API rules it.
      (value) => !value || value >= new Date(new Date().setHours(0, 0, 0, 0)),
    )
    .default(null),
});

export type IGiftVoucherFormModel = yup.InferType<typeof giftVoucherFormSchema>;

export const giftVoucherFormDefaultValues: IGiftVoucherFormModel = {
  name: "",
  amount: undefined as unknown as number,
  validUntil: null,
};

/**
 * The redeem dialog: a scanned or typed code plus the client it is being handed to.
 * Assigning a holder moves NO money — the value stays on the voucher and stays locked to
 * the issuing studio.
 */
export const giftVoucherRedeemFormSchema = yup.object({
  code: yup
    .string()
    .trim()
    .length(10, "Кодът на ваучера е от 10 символа.")
    .required(requiredField("Код")),
  userId: yup
    .number()
    .typeError(requiredField("Клиент"))
    .positive(requiredField("Клиент"))
    .required(requiredField("Клиент")),
});

export type IGiftVoucherRedeemFormModel = yup.InferType<
  typeof giftVoucherRedeemFormSchema
>;

export const giftVoucherRedeemFormDefaultValues: IGiftVoucherRedeemFormModel = {
  code: "",
  userId: undefined as unknown as number,
};

/** The "Изпрати по имейл" section of the details dialog. */
export const giftVoucherEmailFormSchema = yup.object({
  email: yup
    .string()
    .trim()
    .email("Невалиден имейл адрес.")
    .required(requiredField("Имейл")),
});

export type IGiftVoucherEmailFormModel = yup.InferType<
  typeof giftVoucherEmailFormSchema
>;

export const giftVoucherEmailFormDefaultValues: IGiftVoucherEmailFormModel = {
  email: "",
};
